import { useState, useEffect, useRef } from "react";
import axios from "axios";
import apiUrls from "../../../api";

interface SceneScore {
  contentId: number;
  sceneName: string;
  successRate: number;
  attemptCount: number;
  avgDuration: number;
}

interface SceneScoreForChart {
  id: number;
  name: string;
  score: number;
  score_max: number;
}

interface UseEnduserScorePerSceneOptions {
  projectId: number;
  startDate?: string;
  endDate?: string;
}

interface UseEnduserScorePerSceneResult {
  data: SceneScoreForChart[];
  isLoading: boolean;
  error: string | undefined;
  refetch: () => void;
}

/**
 * Hook to fetch enduser-specific score per scene data
 * This uses the enduserScorePerScene endpoint which filters by the logged-in user
 */
export const useEnduserScorePerScene = ({
  projectId,
  startDate,
  endDate,
}: UseEnduserScorePerSceneOptions): UseEnduserScorePerSceneResult => {
  const [data, setData] = useState<SceneScoreForChart[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | undefined>();

  const fetchKeyRef = useRef<string | null>(null);
  const [refetchTrigger, setRefetchTrigger] = useState(0);

  const refetch = () => setRefetchTrigger((prev) => prev + 1);

  useEffect(() => {
    const fetchData = async () => {
      const fetchKey = `enduser-scene-${projectId}-${startDate || "null"}-${
        endDate || "null"
      }-${refetchTrigger}`;
      if (fetchKeyRef.current === fetchKey) return;

      // Wait for auth token to be available (race condition on page refresh)
      const maxWaitTime = 5000;
      const checkInterval = 100;
      let waited = 0;
      while (!axios.defaults.headers.common["Authorization"] && waited < maxWaitTime) {
        await new Promise((resolve) => setTimeout(resolve, checkInterval));
        waited += checkInterval;
      }

      if (!axios.defaults.headers.common["Authorization"]) {
        setIsLoading(false);
        setError("Authentication not ready");
        return;
      }

      fetchKeyRef.current = fetchKey;

      setIsLoading(true);
      setError(undefined);

      try {
        let url = `${apiUrls.stats.enduserScorePerScene}?project=${projectId}`;
        if (startDate) url += `&startDate=${startDate}`;
        if (endDate) url += `&endDate=${endDate}`;

        const response = await axios.get<SceneScore[]>(url);
        const sceneScores = response.data || [];

        // Transform to format expected by SuccessRatePerSceneChart
        // The chart expects: { id, name, score, score_max }
        // API returns: { contentId, sceneName, successRate, attemptCount, avgDuration }
        const chartData: SceneScoreForChart[] = sceneScores.map((scene) => ({
          id: scene.contentId,
          name: scene.sceneName,
          // Convert successRate (0-100) to score/score_max format
          score: scene.successRate,
          score_max: 100,
        }));

        setData(chartData);
      } catch (err) {
        console.error("Error fetching enduser score per scene:", err);
        setError(err instanceof Error ? err.message : "Failed to load scene data");
        setData([]);
      } finally {
        setIsLoading(false);
      }
    };

    if (projectId) {
      void fetchData();
    }
  }, [projectId, startDate, endDate, refetchTrigger]);

  return {
    data,
    isLoading,
    error,
    refetch,
  };
};

export default useEnduserScorePerScene;
